import { faListCheck } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { QuantityPill } from 'components/AddonsMode/QuantityPill/QuantityPill';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { Alert } from 'components/UI/Alert/Alert';
import { IconCircle } from 'components/UI/IconCircle/IconCircle';
import { PromptModal } from 'components/UI/PromptModal/PromptModal';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { getEvaluationServices, getServices } from 'reducers/services/ServicesSelectors';
import { dispatch } from 'store';
import { AddonService } from 'types/AddonService';
import { ReportEvaluationResult } from 'types/Evaluation';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Service } from 'types/Service';
import {
    navigateToStep,
    removeOnlineBookingBookingItems,
    updateOnlineBookingBookingItem
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { getOnlineBookingGroomingItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { isOnlineBookingItemEmpty, OnlineBookingStep } from 'types/OnlineBooking';
import { getBusinessArea, trackServiceSelected } from 'utils/analytics';
import { createDefaultSchedule } from 'utils/addonSchedule';
import { getOnlineBookingAddonUnits } from 'utils/onlineBookingAddons';
import {
    formatServicePriceLabel,
    getAvailableServicesForPet,
    isServiceAvailableForPet
} from 'utils/serviceUtils';
import classes from './OnlineBookingGroomingServiceListStep.module.scss';

export const OnlineBookingGroomingServiceListStep: React.FC = React.memo(() => {
    const marketplace = useMarketplace();

    const services = useSelector(getServices);
    const addonServices = filterAddonsByBusinessAreas(useSelector(getActiveAddonServices), [
        MarketplaceServiceCategory.GROOMING,
        MarketplaceServiceCategory.DENTAL
    ]);
    const evaluationServices = useSelector(getEvaluationServices);
    const groomingBookingItems = useSelector(getOnlineBookingGroomingItems);

    const [showExitConfirmationModal, setShowExitConfirmationModal] =
        React.useState<boolean>(false);
    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        groomingBookingItems[0].uuid
    );

    const isGroomingEvaluationEnabled = React.useMemo(() => {
        return (
            marketplace.evaluationEnabled &&
            evaluationServices.some(
                service =>
                    service.evaluationMetadata?.businessAreas.includes(
                        MarketplaceServiceCategory.GROOMING
                    )
            )
        );
    }, [evaluationServices, marketplace.evaluationEnabled]);

    const selectedPet = React.useMemo(() => {
        return groomingBookingItems.find(item => item.uuid === selectedBookingItemUuid)?.pet;
    }, [groomingBookingItems, selectedBookingItemUuid]);

    // Pet categorization based on evaluation status
    const eligiblePets = React.useMemo(() => {
        if (!isGroomingEvaluationEnabled) {
            return groomingBookingItems.filter(item => {
                return (
                    services
                        .filter(service =>
                            [
                                MarketplaceServiceCategory.GROOMING,
                                MarketplaceServiceCategory.DENTAL
                            ].includes(service.businessArea)
                        )
                        .filter(service => isServiceAvailableForPet(service, item.pet)).length > 0
                );
            });
        }

        return groomingBookingItems.filter(
            item => item.pet.evaluationReport?.groomingResult === ReportEvaluationResult.PASS
        );
    }, [services, groomingBookingItems, isGroomingEvaluationEnabled]);

    const nonEligiblePets = React.useMemo(() => {
        if (!isGroomingEvaluationEnabled) {
            return groomingBookingItems.filter(item => {
                return (
                    services
                        .filter(service =>
                            [
                                MarketplaceServiceCategory.GROOMING,
                                MarketplaceServiceCategory.DENTAL
                            ].includes(service.businessArea)
                        )
                        .filter(service => isServiceAvailableForPet(service, item.pet)).length === 0
                );
            });
        }

        return groomingBookingItems.filter(
            item => item.pet.evaluationReport?.groomingResult === ReportEvaluationResult.FAIL
        );
    }, [services, groomingBookingItems, isGroomingEvaluationEnabled]);

    const petsRequiringEvaluation = React.useMemo(() => {
        if (!isGroomingEvaluationEnabled) return [];

        return groomingBookingItems.filter(
            item =>
                !item.pet.evaluationReport ||
                item.pet.evaluationReport?.groomingResult === ReportEvaluationResult.FAIL
        );
    }, [groomingBookingItems, isGroomingEvaluationEnabled]);

    const selectedBookingItem = React.useMemo(() => {
        return groomingBookingItems.find(item => item.uuid === selectedBookingItemUuid);
    }, [groomingBookingItems, selectedBookingItemUuid]);

    const selectedPetRequiresEvaluation = React.useMemo(() => {
        return (
            isGroomingEvaluationEnabled &&
            petsRequiringEvaluation.some(pet => pet.pet.uuid === selectedPet?.uuid)
        );
    }, [isGroomingEvaluationEnabled, selectedPet, petsRequiringEvaluation]);

    const submitButtonLabel = React.useMemo(() => {
        const isLastPet =
            groomingBookingItems.findIndex(item => item.uuid === selectedBookingItemUuid) ===
            groomingBookingItems.length - 1;

        // Check if there are ANY eligible pets without services
        const hasEligiblePetsWithoutService = eligiblePets
            .filter(item => item.uuid !== selectedBookingItemUuid)
            .some(item => !item.service?.id);

        if (hasEligiblePetsWithoutService || !isLastPet) {
            return 'Next Pet';
        }

        return 'Continue';
    }, [eligiblePets, selectedBookingItemUuid, groomingBookingItems]);

    const isValid = React.useMemo(() => {
        const hasEligiblePetsWithoutService = eligiblePets
            .filter(item => item.uuid !== selectedBookingItemUuid)
            .some(item => !item.service?.id);

        if (selectedPetRequiresEvaluation && hasEligiblePetsWithoutService) {
            return true;
        }

        if (
            !selectedPetRequiresEvaluation &&
            selectedBookingItem?.service?.id &&
            hasEligiblePetsWithoutService
        ) {
            return true;
        }

        return eligiblePets.every(item => item.service?.id);
    }, [eligiblePets, selectedBookingItemUuid, selectedPetRequiresEvaluation, selectedBookingItem]);

    const availableServices = React.useMemo(() => {
        const selectedPet = groomingBookingItems.find(item => item.uuid === selectedBookingItemUuid)
            ?.pet;

        if (!selectedPet) return [];

        return getAvailableServicesForPet(
            services,
            selectedPet,
            MarketplaceServiceCategory.GROOMING
        );
    }, [services, groomingBookingItems, selectedBookingItemUuid]);

    const serviceChangeHandler = (service: Service) => {
        dispatch(updateOnlineBookingBookingItem(selectedBookingItemUuid, { service }));
    };

    // A grooming Add-On has no days, only a quantity, kept as an Everyday schedule (one date).
    const addonChangeHandler = (addon: AddonService, checked: boolean) => {
        let addons = selectedBookingItem?.addons || [];
        const addonSchedules = { ...selectedBookingItem?.addonSchedules };

        if (checked) {
            addons = [...addons, addon];
            addonSchedules[addon.id] = createDefaultSchedule();
        } else {
            addons = addons.filter(a => a.id !== addon.id);
            delete addonSchedules[addon.id];
        }

        dispatch(
            updateOnlineBookingBookingItem(selectedBookingItemUuid, { addons, addonSchedules })
        );
    };

    const addonQuantityChangeHandler = (addon: AddonService, quantity: number) => {
        dispatch(
            updateOnlineBookingBookingItem(selectedBookingItemUuid, {
                addonSchedules: {
                    ...selectedBookingItem?.addonSchedules,
                    [addon.id]: { ...createDefaultSchedule(), quantity }
                }
            })
        );
    };

    const backHandler = React.useCallback(() => {
        const emptyBookingItems = groomingBookingItems.filter(isOnlineBookingItemEmpty);

        if (emptyBookingItems.length === groomingBookingItems.length) {
            dispatch(removeOnlineBookingBookingItems(groomingBookingItems.map(item => item.uuid)));

            return dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
        }

        setShowExitConfirmationModal(true);
    }, [groomingBookingItems]);

    const exitConfirmationModalConfirmHandler = React.useCallback(() => {
        dispatch(removeOnlineBookingBookingItems(groomingBookingItems.map(item => item.uuid)));
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [groomingBookingItems]);

    const trackServiceSelection = () => {
        trackServiceSelected({
            marketplace_slug: marketplace.slug,
            business_area: getBusinessArea(groomingBookingItems),
            service_names: groomingBookingItems
                .map(item => item.service?.name)
                .filter(Boolean)
                .join(', '),
            pets_count: groomingBookingItems.length
        });
    };

    const submitHandler = () => {
        const isLastPet =
            groomingBookingItems.findIndex(item => item.uuid === selectedBookingItemUuid) ===
            groomingBookingItems.length - 1;

        // If current pet requires evaluation, skip to next step
        if (selectedPetRequiresEvaluation) {
            // Remove non-eligible pets and proceed
            if (petsRequiringEvaluation.length > 0) {
                const nonEligibleUuids = petsRequiringEvaluation.map(item => item.uuid);
                dispatch(removeOnlineBookingBookingItems(nonEligibleUuids));
            }

            // Analytics
            trackServiceSelection();

            if (!marketplace.onlineBookingPreferences.allowSelectStaff) {
                return dispatch(navigateToStep(OnlineBookingStep.DatePicker));
            }

            return dispatch(navigateToStep(OnlineBookingStep.StaffList));
        }

        // Check if there are eligible pets that still need services
        const petsWithoutService = eligiblePets.filter(item => !item.service?.id);
        const otherPetsWithoutService = petsWithoutService.filter(
            item => item.uuid !== selectedBookingItemUuid
        );

        if (otherPetsWithoutService.length > 0 || !isLastPet) {
            // Find the current pet index in the full list
            const currentIndex = groomingBookingItems.findIndex(
                item => item.uuid === selectedBookingItemUuid
            );

            // Find the next pet in the full list (cycling through all pets)
            const nextIndex = (currentIndex + 1) % groomingBookingItems.length;
            const nextPetUuid = groomingBookingItems[nextIndex].uuid;

            // Animate the pet chip to center horizontally
            const petChip = document.getElementById(nextPetUuid);
            petChip?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

            setSelectedBookingItemUuid(nextPetUuid);
            return;
        }

        // All eligible pets have services, remove non-eligible pets (those requiring evaluation) and proceed
        if (petsRequiringEvaluation.length > 0 || nonEligiblePets.length > 0) {
            const nonEligibleUuids = [
                ...petsRequiringEvaluation.map(item => item.uuid),
                ...nonEligiblePets.map(item => item.uuid)
            ];
            dispatch(removeOnlineBookingBookingItems(nonEligibleUuids));
        }

        // Analytics
        trackServiceSelection();

        if (!marketplace.onlineBookingPreferences.allowSelectStaff) {
            return dispatch(navigateToStep(OnlineBookingStep.DatePicker));
        }

        dispatch(navigateToStep(OnlineBookingStep.StaffList));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                <div className={classes.petTabs}>
                    {groomingBookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            checked={!!item.service?.id}
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                {availableServices.length === 0 && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        text="There are no services available for your pet. Please contact the business for assistance."
                    />
                )}

                {selectedPetRequiresEvaluation && (
                    <div className="flex flex-1 flex-col items-center justify-center text-center gap-3">
                        <IconCircle size={72} icon={faListCheck} />

                        <h2 className="h3 w-full">This pet needs to pass an evaluation</h2>

                        <p className="text-md mx-auto max-w-sm">
                            Please contact the business to arrange your appointment.
                        </p>
                    </div>
                )}

                {availableServices.length > 0 && !selectedPetRequiresEvaluation && (
                    <>
                        <h1 className="h3 w-full">Grooming Services</h1>

                        <div className="flex flex-col gap-3">
                            {availableServices.map(item => (
                                <RadioCard
                                    key={item.service.id}
                                    title={item.service.name}
                                    description={formatServicePriceLabel(
                                        item.price,
                                        marketplace.onlineBookingPreferences.pricingOption
                                    )}
                                    checked={selectedBookingItem?.service?.id === item.service.id}
                                    content={item.service.description}
                                    onChange={() => serviceChangeHandler(item.service)}
                                />
                            ))}
                        </div>
                    </>
                )}

                {addonServices.length > 0 &&
                    availableServices.length > 0 &&
                    !selectedPetRequiresEvaluation && (
                        <>
                            <h1 className="h3 w-full">Add-ons</h1>

                            <div className="flex flex-col gap-3">
                                {addonServices.map(addon => (
                                    <RadioCard
                                        key={addon.id}
                                        mode="checkbox"
                                        title={addon.name}
                                        checked={selectedBookingItem?.addons.some(
                                            a => a.id === addon.id
                                        )}
                                        description={formatServicePriceLabel(
                                            addon.price,
                                            marketplace.onlineBookingPreferences.pricingOption
                                        )}
                                        onChange={checked => addonChangeHandler(addon, checked)}
                                        checkedAction={
                                            <QuantityPill
                                                label={`${addon.name} quantity`}
                                                value={getOnlineBookingAddonUnits(
                                                    selectedBookingItem!,
                                                    addon
                                                )}
                                                min={0}
                                                onChange={quantity =>
                                                    quantity === 0
                                                        ? addonChangeHandler(addon, false)
                                                        : addonQuantityChangeHandler(
                                                              addon,
                                                              quantity
                                                          )
                                                }
                                            />
                                        }
                                    />
                                ))}
                            </div>
                        </>
                    )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label={submitButtonLabel}
                        disabled={!isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>

            {showExitConfirmationModal && (
                <PromptModal
                    open={true}
                    title="Exit Grooming Services"
                    description="Closing now will discard any unsaved changes. Do you want to exit?"
                    onConfirm={exitConfirmationModalConfirmHandler}
                    onCancel={() => setShowExitConfirmationModal(false)}
                />
            )}
        </TwoColumnsLayout>
    );
});

OnlineBookingGroomingServiceListStep.displayName = 'OnlineBookingGroomingServiceListStep';
